import { MESSAGES } from '../constant/messages';
import { DEFAULT_MIN_COUNT, OPTION_TYPE } from '../constant/product-option';
import type { CustomInputItemType, ItemCardType, ItemListMapValueType } from '../type/common.type';
import type { ItemCardState } from '../type/common.type';
import {
  BogoInfo,
  OptionItemList_item_attribute_list,
  PdpBogoDiscountType,
  PdpDeliveryType,
} from '../type/common.type';
import { DeliveryDateData, ShippingBadgeData } from '../type/common.type';
import { isSameItemCard } from '../util/checkers/item-card-checker';
import { showZDSToastInOption } from '../util/toast/zds-toast-in-option';
import { ceilTens } from '../util/transformers/number-transformer';

export interface ItemCardPayload {
  item_cards: ItemCardState;
  selected_item: ItemListMapValueType;
  bogo_item_list?: ItemListMapValueType[];
  custom_input_list?: CustomInputItemType[];
  context: {
    bogo_info: BogoInfo | null;
    maxPurchaseQuantity?: number;
    min_order_quantity: number;
    is_store_deal_banner: boolean;
    is_order_quantity_item_type: boolean;
    is_order_quantity_product_type: boolean;
    enableSingleItemSelection?: boolean;
  };
}

/**
 * 선택된 아이템을 아이템 카드 데이터로 파싱 후 아이템 카드 리스트에 추가 또는 수량 증가 처리
 * @param item_cards - 아이템 카드 목록
 * @param selected_item - 선택된 아이템
 * @param bogo_item_list - 1+N 프로모션에서 함께 구매되는 아이템 목록
 * @param context - 아이템 카드 목록 생성 컨텍스트
 * @returns 아이템 카드 목록
 */
export const buildItemCard = ({
  item_cards,
  selected_item,
  bogo_item_list = [],
  custom_input_list = [],
  context,
}: ItemCardPayload): ItemCardState => {
  const updated_item_cards = { ...item_cards };
  const selected_item_id = createSafeItemId(selected_item, bogo_item_list);
  const target_index = item_cards.list.findIndex((item) => {
    // createSafeItemId로 생성된 복합 ID를 비교
    return isSameItemCard(
      { id: createSafeItemId(item, item.bogo_item_list), custom_input_list: item.custom_input_list },
      { id: selected_item_id, custom_input_list },
    );
  });

  const is_existing_item = target_index > -1;

  // 스토어딜의 경우 1개만 선택 가능
  if (context.is_store_deal_banner && item_cards.list.length > 0) {
    showZDSToastInOption(MESSAGES.STORE_DEAL_MAX_SELECTION);
    return item_cards;
  }

  // 1개 아이템만 선택 가능한 경우 기존 아이템을 모두 제거하고 새 아이템 추가
  if (context.enableSingleItemSelection && item_cards.list.length > 0) {
    updated_item_cards.list = [];
    updated_item_cards.total_count = 0;
  }

  // 기존 아이템이 있는 경우 수량 증가 처리
  if (is_existing_item && !context.enableSingleItemSelection) {
    const updated_item_cards_list = increaseItemCardCount(
      updated_item_cards.list,
      target_index,
      context.maxPurchaseQuantity,
    );
    updated_item_cards.list = updated_item_cards_list;
    //  신규 아이템 추가
  } else {
    const items = [selected_item, ...bogo_item_list];
    const display_name = createDisplayName(selected_item, bogo_item_list, custom_input_list);
    const min_price = items.reduce((min, item) => Math.min(min, item.price), Infinity);
    const min_count = getMinCountByOrderRule(
      { ...selected_item, count: 0, bogo_item_list: [] },
      updated_item_cards.total_count,
      context.min_order_quantity,
      context.is_order_quantity_product_type,
    );
    const { delivery_type, expected_delivery_date } = getLatestShippingItem({
      bogo_item_list,
      delivery_type: selected_item.delivery_type,
      expected_delivery_date: selected_item.expected_delivery_date,
    });

    const calculatedPrice = getItemPrice({
      items,
      min_price,
      default_price: selected_item.price,
      bogo_info: context.bogo_info,
    });

    const bogoItemListWithDefaultPrice = bogo_item_list.map((item) => ({
      ...item,
      price: calculatedPrice,
    }));

    const new_item = {
      ...selected_item,
      display_name,
      delivery_type,
      expected_delivery_date,
      count: min_count,
      price: calculatedPrice,
      minimum_order_quantity: context.is_order_quantity_item_type && min_count > 1 ? min_count : 0,
      bogo_item_list: bogoItemListWithDefaultPrice,
      custom_input_list,
    };
    updated_item_cards.list.push(new_item);
  }

  return {
    list: updated_item_cards.list,
    total_count: updated_item_cards.list.reduce((sum, item) => sum + item.count, 0),
  };
};

// 주문 규칙에 따른 최소 수량 계산
export const getMinCountByOrderRule = (
  item: Omit<ItemCardType, 'display_name' | 'minimum_order_quantity'>,
  item_total_count: number,
  min_order_quantity: number,
  is_order_quantity_product_type: boolean,
): number => {
  // 다른 아이템들의 수량을 제외한 나머지 필요 수량 계산
  const other_items_count =
    item_total_count > item.count ? item_total_count - item.count : item.count - item_total_count;
  const required_quantity = other_items_count > min_order_quantity ? 1 : min_order_quantity - other_items_count;
  const is_additional_option = item.option_type === OPTION_TYPE.ADDITIONAL;

  // 주문 수량 상품 타입이고 추가 옵션이 아닌 경우
  if (!is_additional_option && is_order_quantity_product_type) {
    return required_quantity === 0 ? 1 : required_quantity;
  }

  // 일반적인 경우
  return !is_additional_option && min_order_quantity && min_order_quantity > DEFAULT_MIN_COUNT
    ? min_order_quantity
    : DEFAULT_MIN_COUNT;
};

// 아이템 카드 배송 표기 데이터 계산
export const getItemCardShippingData = (item: ItemCardType) => {
  let badge: ShippingBadgeData = { type: null };
  let delivery_date: DeliveryDateData | null = null;

  if (item.delivery_type === PdpDeliveryType.VENDOR_FLEX) {
    badge = { type: 'store_zigzin' };
  } else if (item.delivery_type === PdpDeliveryType.ZIGZIN) {
    badge = { type: 'zigzin' };
  } else if (item.delivery_type === PdpDeliveryType.FAST) {
    badge = { type: 'fast_shipping' };
  }

  if (item.expected_delivery_date) {
    let color_type: DeliveryDateData['color_type'] = 'default';

    if (item.delivery_type === PdpDeliveryType.VENDOR_FLEX) {
      color_type = 'store_zigzin';
    } else if (item.delivery_type === PdpDeliveryType.ZIGZIN) {
      color_type = 'zigzin';
    } else if (item.delivery_type === PdpDeliveryType.FAST) {
      color_type = 'fast_shipping';
    }

    delivery_date = { content: item.expected_delivery_date, color_type };
  }

  return { badge, delivery_date };
};

// 선택된 아이템 ID 배열 생성
export const createSafeItemId = (item: ItemListMapValueType, bogo_item_list: ItemListMapValueType[] = []) => {
  return `${item.id}|${bogo_item_list.map((bogo_item) => bogo_item.id).join(',')}`;
};

// 아이템의 표시 이름 생성
const createDisplayName = (
  item_card: ItemListMapValueType,
  bogo_item_list: ItemListMapValueType[] = [],
  custom_input_list: CustomInputItemType[] = [],
): string => {
  const isAdditionalOption = item_card.option_type === OPTION_TYPE.ADDITIONAL;
  const additionalText = isAdditionalOption ? `[추가상품] ` : '';
  const attributeText = formatItemAttributeValues(item_card.item_attribute_list);
  const bogoText = bogo_item_list.length
    ? `, ${bogo_item_list.map((o) => formatItemAttributeValues(o.item_attribute_list)).join(', ')}`
    : '';

  const customInputTextList: string[] = [];

  custom_input_list.forEach((custom_input) => {
    customInputTextList.push(Object.values(custom_input)[0]);
  });

  const customInputText = customInputTextList.join(' / ');

  const separator = (attributeText || bogoText) && customInputText ? ' / ' : '';

  return additionalText + attributeText + bogoText + separator + customInputText || '기본옵션';
};

// 기존 아이템 수량 증가 처리
const increaseItemCardCount = (
  prev_list: ItemCardType[],
  item_index: number,
  maximum_count_limit?: number,
): ItemCardType[] => {
  const existing_item = prev_list[item_index];

  // 최대 수량 제한 체크
  if (maximum_count_limit && existing_item.count >= maximum_count_limit) {
    showZDSToastInOption(MESSAGES.EXCEED_MAXIMUM_LIMIT);
    return prev_list;
  }

  showZDSToastInOption(MESSAGES.DUPLICATE_SELECTION);

  const updated_list = [...prev_list];
  updated_list[item_index] = { ...existing_item, count: existing_item.count + 1 };

  return updated_list;
};

// 가장 늦게 배송되는 아이템의 배송 정보를 반환합니다.
const getLatestShippingItem = ({
  bogo_item_list,
  delivery_type,
  expected_delivery_date,
}: {
  bogo_item_list: ItemListMapValueType[];
  delivery_type: PdpDeliveryType | null;
  expected_delivery_date: string | null;
}) => {
  if (!bogo_item_list.length) {
    return { delivery_type, expected_delivery_date };
  }

  // 아이템 중 가장 늦은 배송일을 가진 아이템 찾기
  const latest_item = bogo_item_list.reduce((latest, current) => {
    const latest_date = latest.expected_delivery_time ?? 0;
    const current_date = current.expected_delivery_time ?? 0;
    return latest_date > current_date ? latest : current;
  });

  return {
    delivery_type: latest_item?.delivery_type,
    expected_delivery_date: latest_item?.expected_delivery_date ?? null,
  };
};

// 아이템 속성 값 조인
const formatItemAttributeValues = (item_attribute_list: OptionItemList_item_attribute_list[]) => {
  return item_attribute_list.map((o) => o.value).join(' / ');
};

// 아이템 가격 계산
const getItemPrice = ({
  items,
  min_price,
  default_price,
  bogo_info,
}: {
  items: ItemListMapValueType[];
  min_price: number;
  default_price: number;
  bogo_info: BogoInfo | null;
}) => {
  if (!bogo_info?.required_quantity) {
    return default_price;
  }
  return getBogoDiscountedPrice(min_price, bogo_info) * items.length;
};

// 1+N 정액/정률 할인 기준에 따른 금액 정보를 가져오는 함수, 정액/정률 할인 기준에 따라 기본 금액에서 할인시킨다.
const getBogoDiscountedPrice = (price: number, bogo_info: BogoInfo | null) => {
  let refined_price = price / ((bogo_info?.required_quantity ?? 0) + 1);

  if (!bogo_info?.discount_type) {
    return refined_price;
  }

  // 정액할인
  if (bogo_info.discount_type === PdpBogoDiscountType.AMOUNT) {
    const discount_amount = bogo_info.discount_amount ?? 0;
    refined_price = price - discount_amount;
  }

  // 정률할인
  if (bogo_info.discount_type === PdpBogoDiscountType.RATE) {
    const BASIS_POINT = 10000;
    const rate = (BASIS_POINT - (bogo_info.discount_rate_bp ?? 0)) / BASIS_POINT;
    refined_price = price * rate;
  }

  return ceilTens(refined_price);
};
